import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterModule } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { ResumeListItem } from '@edtech-ui/core/models/resume-builder/get-user-resume';
import { IstDatePipe } from '@edtech-ui/core/pipes/time-resume-date.pipe';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-resumer-dashboard',
	standalone: true,
	imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule, MatProgressSpinnerModule, HasPermissionDirective,
		IstDatePipe
	],
	templateUrl: './resumer-dashboard.component.html',
	styleUrl: './resumer-dashboard.component.scss'
})
export class ResumerDashboardComponent implements OnInit, OnDestroy {

	studentsResume: ResumeListItem[] = [];
	subscription = new Subscription();
	editingResumeId: string | null = null;
	editedResumeName: string = '';
	page = 1;
	pageSize = 10;
	totalPages = 0;
	resumeCount: number = 0;
	isLoading = false;
	PermissionForStudents = PermissionForStudents;

	constructor(
		private router: Router,
		private resumeService: ResumeService,
		private _toastr: ToastrService
	) { }

	ngOnInit(): void {
		this.getStudentResume(true);
	}

	getStudentResume(loadMore = false) {

		this.isLoading = true;
		this.subscription.add(
			this.resumeService.getStudentsResume(this.page, this.pageSize).pipe(
				tap(response => {
					if (response.Success) {
						this.resumeCount = response.Data.total_count;
						this.totalPages = response.Data.total_pages;
						if (loadMore) {
							this.studentsResume = [...this.studentsResume, ...response.Data.resumes];
						} else {
							this.studentsResume = response.Data.resumes;
						}
					}
					else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong.Please try again');
					return of(null);
				})
			).subscribe()
		)
	}


	deleteStudentResume(resumeId: string) {
		this.subscription.add(
			this.resumeService.deleteStudentResume(resumeId).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success(response.Message);
						this.page = 1;
						this.studentsResume = [];
						this.getStudentResume(true);
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong.Please try again');
					return of(null);
				})
			).subscribe()
		)
	}


	updateResumeName(resumeId: string) {
		const payload = {
			resume_name: this.editedResumeName
		}
		this.subscription.add(
			this.resumeService.updateStudentResume(resumeId, payload).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success('Resume name update successfully');
						this.page = 1;
						this.studentsResume = [];
						this.getStudentResume(true);
						const index = this.studentsResume.findIndex(
							(r: ResumeListItem) => r.resume_id === resumeId
						);

						if (index !== -1) {
							this.studentsResume[index].resume_name = this.editedResumeName;
						}
						this.editingResumeId = null;
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong');
					return of(null);
				})
			).subscribe()
		);
	}


	openEdit(resume: ResumeListItem) {
		this.editingResumeId = resume.resume_id;
		this.editedResumeName = resume.resume_name;
	}


	cancelEdit() {
		this.editingResumeId = null;
		this.editedResumeName = '';
	}


	goToResumeDetailPage(resume_id: string) {
		this.router.navigate(['/student-dashboard/resume-preview', resume_id]);
	}


	loadMore() {
		if (this.page < this.totalPages) {
			this.page++;
			this.getStudentResume(true);
		}
	}


	goToResumebuilderPage() {
		this.resumeService.resetResumeBuilder();
		this.router.navigate(['/student-dashboard/resume-builder']);
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe()
	}

}
